import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getReward, getCurrentMember, redeemReward, isAuthenticated, Reward, Member } from '../services/api';
import { Lock, Sparkles, BadgePercent, Star, Gift, Calendar, Package, Trophy, Medal, Award, CheckCircle, ArrowLeft, User } from 'lucide-react';

const RewardDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { rewardId } = useParams<{ rewardId: string }>();
  
  const [reward, setReward] = useState<Reward | null>(null);
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [requestedDate, setRequestedDate] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [redemptionCode, setRedemptionCode] = useState('');

  useEffect(() => {
    loadData();
  }, [rewardId]);

  const loadData = async () => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      const [rewardData, memberData] = await Promise.all([
        getReward(rewardId!),
        getCurrentMember()
      ]);
      setReward(rewardData.reward);
      setMember(memberData);
      
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 7);
      setRequestedDate(defaultDate.toISOString().split('T')[0]);
    } catch (err) {
      console.error('Failed to load reward:', err);
      navigate('/rewards');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!reward || !member) return;

    setRedeeming(true);
    try {
      const response = await redeemReward(
        reward.rewardId,
        reward.requiresScheduling ? requestedDate : undefined
      );

      if (response.success) {
        setRedemptionCode(response.redemption.redemptionCode);
        setShowSuccess(true);
        setMember({ ...member, currentPoints: response.memberPointsRemaining });
      }
    } catch (err: any) {
      alert(err.message || 'Failed to redeem reward');
    } finally {
      setRedeeming(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Access': return Lock;
      case 'Experience': return Sparkles;
      case 'Discount': return BadgePercent;
      case 'Service': return Star;
      default: return Gift;
    }
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'Gold': return Trophy;
      case 'Silver': return Medal;
      default: return Award;
    }
  };

  const getTierBadgeColor = (tier: string) => {
    switch (tier) {
      case 'Gold': return 'bg-yellow-500 text-yellow-900';
      case 'Silver': return 'bg-gray-400 text-gray-900';
      default: return 'bg-amber-700 text-white';
    }
  };

  const getMinDate = () => {
    if (!reward?.requiresScheduling || !reward.advanceNoticeDays) return '';
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + reward.advanceNoticeDays);
    return minDate.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-buggy-blue mx-auto mb-4"></div>
          <p className="text-buggy-gray">Loading...</p>
        </div>
      </div>
    );
  }

  if (!reward || !member) return null;

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={48} strokeWidth={2} className="text-white" />
          </div>
          <h2 className="text-2xl font-bold text-buggy-dark mb-2">Reward Redeemed!</h2>
          <p className="text-buggy-gray mb-6">Your redemption code:</p>
          
          <div className="bg-buggy-blue text-white p-6 rounded-lg mb-6">
            <p className="text-3xl font-bold tracking-wider">{redemptionCode}</p>
          </div>

          <p className="text-sm text-buggy-gray mb-6">
            {reward.requiresScheduling 
              ? `Show this code at the store on ${new Date(requestedDate).toLocaleDateString()}`
              : 'Show this code at checkout to claim your reward'
            }
          </p>

          <div className="space-y-3">
            <button
              onClick={() => navigate('/redemptions')}
              className="w-full bg-buggy-blue text-white font-semibold py-3 px-4 rounded-lg hover:bg-buggy-blue-light transition-colors"
            >
              View My Redemptions
            </button>
            <button
              onClick={() => navigate('/home')}
              className="w-full bg-gray-200 text-buggy-dark font-semibold py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const CategoryIcon = getCategoryIcon(reward.rewardCategory || 'Service');
  const TierIcon = getTierIcon(reward.minTierRequired);

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="bg-buggy-blue text-white p-4">
        <div className="max-w-md mx-auto flex items-center">
          <button
            onClick={() => navigate('/rewards')}
            className="mr-4 hover:opacity-75 flex items-center"
          >
            <ArrowLeft size={24} strokeWidth={2} />
          </button>
          <h1 className="text-xl font-bold">Reward Details</h1>
        </div>
      </div>

      <div className="max-w-md mx-auto p-4">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-buggy-blue to-buggy-blue-light p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <CategoryIcon size={48} strokeWidth={2} />
              <span className={`text-xs px-3 py-1 rounded-full flex items-center gap-1 ${getTierBadgeColor(reward.minTierRequired)}`}>
                <TierIcon size={14} strokeWidth={2.5} />
                {reward.minTierRequired} Tier
              </span>
            </div>
            <h2 className="text-2xl font-bold mb-2">{reward.rewardName}</h2>
            <p className="text-sm opacity-90">{reward.rewardCategory}</p>
          </div>

          <div className="p-6">
            <p className="text-buggy-gray mb-6">{reward.rewardDescription}</p>

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-buggy-gray mb-1">Cost</p>
              {reward.pointsCost > 0 ? (
                <p className="text-2xl font-bold text-buggy-blue">{reward.pointsCost} points</p>
              ) : (
                <p className="text-lg font-semibold text-green-600">Free Benefit</p>
              )}
            </div>

            <div className="space-y-3 mb-6">
              {reward.requiresScheduling && (
                <div className="flex items-start">
                  <Calendar size={24} strokeWidth={2} className="text-buggy-blue mr-3 mt-0.5" />
                  <div>
                    <p className="font-semibold text-buggy-dark">Advance Booking Required</p>
                    <p className="text-sm text-buggy-gray">
                      Must be scheduled {reward.advanceNoticeDays} days in advance
                    </p>
                  </div>
                </div>
              )}

              {reward.availableQuantity && (
                <div className="flex items-start">
                  <Package size={24} strokeWidth={2} className="text-buggy-blue mr-3 mt-0.5" />
                  <div>
                    <p className="font-semibold text-buggy-dark">Limited Availability</p>
                    <p className="text-sm text-buggy-gray">
                      {reward.remainingQuantity} of {reward.availableQuantity} remaining
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-start">
                <User size={24} strokeWidth={2} className="text-buggy-blue mr-3 mt-0.5" />
                <div>
                  <p className="font-semibold text-buggy-dark">Your Status</p>
                  <p className="text-sm text-buggy-gray">
                    Current Tier: {member.currentTier} | Points: {member.currentPoints}
                  </p>
                </div>
              </div>
            </div>

            {reward.requiresScheduling && reward.isEligible && reward.canAfford && (
              <div className="mb-6">
                <label htmlFor="requestedDate" className="block text-sm font-medium text-buggy-dark mb-2">
                  Select Date
                </label>
                <input
                  type="date"
                  id="requestedDate"
                  value={requestedDate}
                  onChange={(e) => setRequestedDate(e.target.value)}
                  min={getMinDate()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-buggy-blue focus:border-transparent"
                />
                <p className="text-xs text-buggy-gray mt-1">
                  Earliest available: {new Date(getMinDate()).toLocaleDateString()}
                </p>
              </div>
            )}

            {/* What you need to unlock - for locked perks */}
            {!reward.isEligible && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex items-start mb-3">
                  <Lock size={20} strokeWidth={2} className="text-amber-600 mr-2 mt-0.5" />
                  <p className="text-amber-800 text-sm font-semibold">
                    What you need to unlock this:
                  </p>
                </div>
                <div className="ml-7 space-y-2">
                  <div className="flex items-center gap-2">
                    <TierIcon size={16} strokeWidth={2} className="text-amber-600" />
                    <p className="text-sm text-amber-800">
                      Reach <strong>{reward.minTierRequired}</strong> tier
                      {reward.minTierRequired === 'Silver' && ' (300 points)'}
                      {reward.minTierRequired === 'Gold' && ' (600 points)'}
                    </p>
                  </div>
                  <p className="text-xs text-amber-700">
                    You're currently {member.currentTier} with {member.currentPoints} points
                    {reward.minTierRequired === 'Silver' && member.currentTier === 'Bronze' && 
                      ` — ${300 - member.currentPoints} more points to go!`
                    }
                    {reward.minTierRequired === 'Gold' && member.currentTier === 'Bronze' && 
                      ` — ${600 - member.currentPoints} more points to go!`
                    }
                    {reward.minTierRequired === 'Gold' && member.currentTier === 'Silver' && 
                      ` — ${600 - member.currentPoints} more points to go!`
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Need more points - for points rewards */}
            {reward.isEligible && !reward.canAfford && reward.pointsCost > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                <div className="flex items-start mb-2">
                  <Gift size={20} strokeWidth={2} className="text-orange-600 mr-2 mt-0.5" />
                  <p className="text-orange-800 text-sm font-semibold">
                    You need more points:
                  </p>
                </div>
                <div className="ml-7">
                  <p className="text-sm text-orange-800">
                    This reward costs <strong>{reward.pointsCost} points</strong>
                  </p>
                  <p className="text-xs text-orange-700 mt-1">
                    You have {member.currentPoints} points — {reward.pointsCost - member.currentPoints} more to go!
                  </p>
                </div>
              </div>
            )}

            <button
              onClick={handleRedeem}
              disabled={!reward.isEligible || !reward.canAfford || redeeming}
              className="w-full bg-buggy-blue text-white font-semibold py-4 px-4 rounded-lg hover:bg-buggy-blue-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {redeeming ? 'Redeeming...' : (
                <>
                  <Gift size={20} strokeWidth={2} />
                  {reward.isEligible && reward.canAfford ? 'Redeem Now' : 'Not Available'}
                </>
              )}
            </button>

            {reward.isEligible && reward.canAfford && reward.pointsCost > 0 && (
              <p className="text-xs text-center text-buggy-gray mt-3">
                Points will be deducted immediately
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardDetailPage;
